/**
 * Espirito Underwriting FNOL demo (CUR-217) — a duplicate of the public claim
 * lodgement flow whose first step is a vehicle + driver lookup instead of the
 * policy-holder page, with the standalone Driver card removed. This is a
 * throwaway demo route: it intentionally duplicates the orchestrator so the live
 * `/public/$orgId/claim/lodgement/...` flow is left completely untouched, and it
 * adds no new persistence (the matched driver flows through the existing
 * submission as the primary contact; vehicle details are display-only).
 *
 * The Payment details and Vehicle details (product data entry) steps are removed
 * from this flow (CUR-236) — the lookup already establishes the vehicle,
 * and payment details are not part of the demo script.
 *
 * The flow ends on a review and declaration step (CUR-240): lookup →
 * questionnaire → additional info → review & declaration (submits) →
 * confirmation. The standalone Conditions step is folded into the review page.
 *
 * PoC demo — hard-blocked on production by the `beforeLoad` guard below, but left
 * reachable on dev and staging where the demo is run. A PostHog flag isn't used:
 * this is a public, unauthenticated route, so there's no identified user/org for
 * PostHog to target (flags would only evaluate anonymously), whereas the
 * environment check is deterministic. Nothing links here — direct URL is the only
 * way in — so guarding this one route is sufficient.
 */
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { confirmAction } from '@client/components/_ui/feedback/modal';
import { AdditionalInfo } from '@client/components/claims/lodgement/AdditionalInfo';
import { Confirmation } from '@client/components/claims/lodgement/public/Confirmation';
import {
    formatFleetRegistration,
    formatFleetVehicleName,
} from '@client/components/claims/lodgement/public/fleet-lookup-format';
import { ReviewDeclaration } from '@client/components/claims/lodgement/public/ReviewDeclaration';
import {
    type FmisVerifiedMatch,
    VehicleDriverLookup,
} from '@client/components/claims/lodgement/public/VehicleDriverLookup';
import { Questionnaire } from '@client/components/claims/questionnaire';
import { PublicThemeProvider } from '@client/components/public-theme-provider';
import { getConfig } from '@client/config/runtime';
import { usePublicClaimFormState } from '@client/hooks/business/claims/usePublicClaimFormState';
import { useServerCheckpoint } from '@client/hooks/business/claims/useServerCheckpoint';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFavIcon } from '@client/hooks/ui/useFavIcon';
import { useQuestionnaireServiceStore } from '@client/stores/questionnaire/questionnaire-service-store';
import { trpcOptions } from '@client/trpc/client';
import { debounce } from '@client/utils';
import { capture } from '@client/utils/analytics';
import { buildIncidentLocationSubmission } from '@client/utils/domain/incident-location';
import { parseBigintRouteParam } from '@client/utils/routing/parse-bigint-route-param';
import { QUESTIONNAIRE_MAPPED_FIELDS } from '@shared/constants';
import type { PolicyHolderContact_depr } from '@shared/types';
import { filesToFileCollections } from '@shared/utils/core/file';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { TRPCClientError } from '@trpc/client';
import { Col, ConfigProvider, Divider, Result, Row, theme } from 'antd';
import type { AliasToken } from 'antd/es/theme/interface';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { useStore } from 'zustand';
import { questionnaireFormTheme } from '../questionnaire-form-theme';
import { applySubmissionResult } from './apply-submission-result';
import { isSafeBackButtonUrl } from './back-button-url';
import { resetLodgementProgress } from './reset-lodgement-progress';

function DemoNotAvailable() {
    return (
        <div className="flex flex-1 items-center justify-center bg-muted p-4">
            <Result status="404" title="Not available" subTitle="This claim form is not available." />
        </div>
    );
}

// Step indices of this shortened demo flow (CUR-236 removed payment/product steps,
// CUR-240 replaced the Conditions step with the trailing review & declaration).
const LOOKUP_FORM = 0;
const QUESTIONNAIRE_FORM = 1;
const ADDITIONAL_INFO_FORM = 2;
const REVIEW_FORM = 3;
const CONFIRMATION_FORM = 4;

// Browser-snapshot namespace for this flow. The org/pds/questionnaire/product tuple alone is
// shared with the live lodgement flow, whose steps are numbered differently — see the
// checkpoint comment below.
const LOOKUP_STORAGE_SURFACE = 'lodgement-lookup' as const;

function ClaimLodgementLookup() {
    const questionnaireStore = useQuestionnaireServiceStore();
    const navigate = useNavigate();
    const { orgId, questionnaireId, productTypeId, pdsVersionId } = Route.useParams();
    // Set by the vehicle-scan provider's completion redirect: Curium redeems the handoff
    // token server-side and lands the claimant back here with the credentials for the
    // silent checkpoint their claim was saved into, so the same-tab round-trip survives
    // without depending on browser storage.
    const { draftId, accessKey } = Route.useSearch();

    const {
        store: formStore,
        loading,
        readinessError,
        retryReadiness,
    } = usePublicClaimFormState({
        orgId,
        pdsVersionId,
        questionnaireId,
        productTypeId,
        draftId,
        accessKey,
        // Namespace this flow's browser snapshots away from the live lodgement flow's —
        // see the storageScope comment below.
        surface: LOOKUP_STORAGE_SURFACE,
    });

    // Clamp to the last step: restored state written by the full lodgement flow — a server
    // draft reached through the URL credentials, or a snapshot left by a build older than
    // the storage namespacing below — can carry an activeForm beyond this shortened flow,
    // which would render a blank step.
    const activeForm = useStore(formStore, (s) => Math.min(s.ui.activeForm, CONFIRMATION_FORM));
    const questionnaireLogoUrl = useStore(formStore, (s) => s.ui.questionnaireLogoUrl);
    const questionnaireFaviconUrl = useStore(formStore, (s) => s.ui.questionnaireFaviconUrl);
    const backButtonLabel = useStore(formStore, (s) => s.ui.backButtonLabel);
    const backButtonUrl = useStore(formStore, (s) => s.ui.backButtonUrl);
    const uiTheme = useStore(formStore, (s) => s.ui.theme);
    const readOnly = useStore(formStore, (s) => s.getReadOnly());
    const questionnaireDeclarationTitle = useStore(formStore, (s) => s.ui.questionnaireDeclarationTitle);
    const questionnaireDeclarationBody = useStore(formStore, (s) => s.ui.questionnaireDeclarationBody);
    const questionnaireConfirmationBody = useStore(formStore, (s) => s.ui.questionnaireConfirmationBody);
    const questionnaireConfirmationTemplateData = useStore(
        formStore,
        (s) => s.ui.questionnaireConfirmationTemplateData,
    );
    const claimGeneratedId = useStore(formStore, (s) => s.claimGeneratedId);
    const claimNotResultOfIllegalAct = useStore(
        formStore,
        (s) => s.claim.jsonData?.claimNotResultOfIllegalAct ?? false,
    );
    const additionalInfo = useStore(formStore, (s) => s.claim.additionalInfo ?? '');
    const claimantRole = useStore(formStore, (s) => s.claim.jsonData?.claimantRole);
    const fleetVehicleSnapshot = useStore(formStore, (s) => s.claim.jsonData?.fleetVehicleSnapshot);
    const contacts = useStore(formStore, (s) => s.claim.contacts);
    // Review-page fallback for the driver details: the verified match lives in
    // route state and is lost on refresh, but the lookup stamped the driver as
    // the primary policy-holder contact.
    const primaryContact = Object.values(contacts ?? {}).find(
        (c): c is PolicyHolderContact_depr => c.type === 'policy_holder' && !!c.isPrimary,
    );

    const { data: isPdsVersionEnabled } = useQuery(
        trpcOptions.pdsVersion.isPdsVersionEnabled.queryOptions({ id: pdsVersionId, orgId }, { initialData: true }),
    );
    const { mutateAsync: publicClaimSubmission } = useMutation(
        trpcOptions.claim.publicClaimSubmission.mutationOptions({
            retry: false,
            trpc: { context: { captcha: true, captchaSingleUse: true } },
        }),
    );

    const [isInitialized, setIsInitialized] = useState(false);
    const [verifiedMatch, setVerifiedMatch] = useState<FmisVerifiedMatch | null>(null);
    // Set when an Edit affordance on the review step jumps back: the next forward
    // exit of any step returns straight to the review instead of walking the
    // remaining steps again.
    const [returningToReview, setReturningToReview] = useState(false);
    // Bumped by "Start again" to force the step container to remount — restarting
    // while already on the lookup step keeps activeForm at 0, so the key alone
    // would not clear the step's local input state.
    const [resetVersion, setResetVersion] = useState(0);
    // A submission is in flight. "Start again" is disabled while it is, so the claimant cannot
    // discard a claim that is already being lodged. Set on the click, not inside the submit's
    // debounce, so it engages the moment the page starts showing a lodging spinner;
    // submissionFenceRef below is the backstop for a reset that still slips in.
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Bumped by every reset. `save` captures it on the click and the resolved submission
    // compares it again, dropping a late-resolving submission's identifiers rather than
    // writing them over a form the claimant has since cleared.
    const submissionFenceRef = useRef(0);

    useFavIcon(questionnaireFaviconUrl || questionnaireLogoUrl);

    const submissionId = useStore(formStore, (s) => s.claim.submissionId);

    // This flow's own browser-snapshot scope. The org/pds/questionnaire/product tuple is
    // NOT unique to a flow — the live lodgement flow runs the same one — so the `surface`
    // suffix namespaces the keys apart. Without it the readiness load restores the live
    // flow's snapshot into this differently-numbered flow, and "Start again" deletes the
    // live flow's recovery snapshot out from under a claimant sharing the browser.
    const storageScope = useMemo(
        () => ({
            orgId,
            pdsVersionId,
            questionnaireId,
            productTypeId,
            surface: LOOKUP_STORAGE_SURFACE,
        }),
        [orgId, pdsVersionId, questionnaireId, productTypeId],
    );

    /**
     * Silent server checkpoint backing the vehicle scan's same-tab handoff. This flow
     * deliberately keeps no browser-storage snapshot of its own: the server checkpoint needs
     * no storage at all, is immune to privacy-browser storage blocking, and is all the
     * same-tab gate requires; if it fails the card still falls back to opening the scan in a
     * new tab. (Its snapshot scope is namespaced by `surface` regardless, so neither a write
     * nor a clear here can reach the live flow's keys.)
     */
    const { beginCheckpointJourney, checkpointCredentialsRef, invalidateCheckpoint, persistServerCheckpoint } =
        useServerCheckpoint({
            formStore,
            questionnaireStore,
            loading,
            isInitialized,
            alreadySubmitted: Boolean(claimGeneratedId),
            urlDraftId: draftId,
            urlAccessKey: accessKey,
            submittedFormIndex: CONFIRMATION_FORM,
            // This flow has no "Save draft", so a draft identity in its form state never
            // originates here — it can only have been restored (from a returning claimant's
            // URL credentials, which are consulted below anyway, or from a snapshot written
            // under the old shared scope). Adopting it would only add a path for a foreign
            // draft to be check-pointed over.
            adoptFormStateDraft: false,
        });

    const { service, jumpToSection } = questionnaireStore.getState();
    const { goToNextForm, goToPrevForm, updateClaim, incrementActiveStep, decrementActiveStep, setActiveForm } =
        formStore.getState();

    const goNextOrReturnToReview = () => {
        if (returningToReview) {
            setReturningToReview(false);
            setActiveForm(REVIEW_FORM);
        } else {
            goToNextForm();
        }
    };

    // Leaving a step backwards cancels the express return — the claimant has
    // chosen to re-walk, so forward navigation resumes normal step order.
    // (Backing between questionnaire cards stays within the step and keeps it.)
    const goToPrevFormAndCancelReturn = () => {
        setReturningToReview(false);
        goToPrevForm();
    };

    // Mirrors the live lodgement flow's reset (see the sibling route). The storage and store
    // clearing lives in reset-lodgement-progress.ts so the keys it touches — and the live
    // flow's keys it must not — are unit-testable; the React-only remainder stays here.
    function startAgain() {
        resetLodgementProgress({
            storageScope,
            urlDraftId: draftId,
            checkpointDraftId: checkpointCredentialsRef.current?.draftId,
            invalidateCheckpoint,
            submissionFenceRef,
            formStore,
            questionnaireStore,
        });
        setVerifiedMatch(null);
        setReturningToReview(false);
        setResetVersion((prev) => prev + 1);

        // Drop the scan-handoff credentials from the URL so a reload cannot
        // rehydrate the cleared checkpoint draft.
        navigate({
            to: '/public/$orgId/claim/lodgement-lookup/$pdsVersionId/$questionnaireId/$productTypeId',
            params: {
                orgId,
                pdsVersionId,
                questionnaireId,
                productTypeId,
            },
            search: {},
            replace: true,
        });
    }

    async function handleStartAgain() {
        const confirmed = await confirmAction({
            title: 'Start again',
            content: 'This will clear your saved progress on this device and restart the claim lodgement form.',
            okText: 'Start again',
            cancelText: 'Cancel',
        });

        if (!confirmed) {
            return;
        }

        startAgain();
        message.success('Progress cleared.');
    }

    useEffect(() => {
        if (loading || readinessError) {
            return;
        }
        const state = formStore.getState();
        if (state.claim.formData) {
            questionnaireStore.getState().initAndContinue(state.claim.formData, state.questionnaireDef);
        } else {
            questionnaireStore.getState().setQuestionnaireDef(state.questionnaireDef);
        }
        setIsInitialized(true);
    }, [loading, readinessError, formStore, questionnaireStore]);

    const baseFontSize = uiTheme.fontSize || 16;
    const { defaultAlgorithm, defaultSeed } = theme;
    const defaultTokens = defaultAlgorithm(defaultSeed);
    const newTheme: Partial<AliasToken> = {
        ...defaultTokens,
        colorPrimaryHover: uiTheme.colorPrimary,
        colorPrimaryActive: uiTheme.colorPrimary,
        ...uiTheme,
    };

    // `submissionFence` is the journey this submission belongs to, captured by `save` on the
    // click rather than here — see submissionFenceRef above.
    const submitClaim = debounce(async (submissionFence: number) => {
        try {
            const isoDateOfLoss = service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.DATE_OF_LOSS) as string;
            // Explicitly mapped questionnaire fields win; the location card fills the gaps
            const incidentLocation = buildIncidentLocationSubmission(service);
            const currentClaim = formStore.getState().claim;
            const result = await publicClaimSubmission({
                causeOfLoss: service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.CAUSE_OF_LOSS) as string,
                addressLine1OfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.ADDRESS_LINE_1_OF_LOSS) as string) ||
                    incidentLocation.addressLine1OfLoss,
                addressLine2OfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.ADDRESS_LINE_2_OF_LOSS) as string) ||
                    incidentLocation.addressLine2OfLoss,
                suburbOfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.SUBURB_OF_LOSS) as string) ||
                    incidentLocation.suburbOfLoss,
                stateOfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.STATE_OF_LOSS) as string) ||
                    incidentLocation.stateOfLoss,
                postcodeOfLoss:
                    (service.getFieldValue(QUESTIONNAIRE_MAPPED_FIELDS.POSTCODE_OF_LOSS) as string) ||
                    incidentLocation.postcodeOfLoss,
                orgId,
                questionnaireId,
                productTypeId,
                pdsVersionId,
                formData: service.root ? service.root.toJS() : undefined,
                files: filesToFileCollections(service.files),
                jsonData: {
                    ...currentClaim.jsonData,
                    questionnaireDeclarationTitle: formStore.getState().ui.questionnaireDeclarationTitle,
                    questionnaireDeclarationBody: formStore.getState().ui.questionnaireDeclarationBody,
                    incidentLocation: incidentLocation.incidentLocation,
                },
                contacts: currentClaim.contacts,
                policyNo: currentClaim.policyNo,
                phITC: currentClaim.phITC,
                phAccountBsb: currentClaim.phAccountBsb,
                phAccountNo: currentClaim.phAccountNo,
                additionalInfo: currentClaim.additionalInfo,
                phAccountName: currentClaim.phAccountName,
                phIsRegisteredForGst: currentClaim.phIsRegisteredForGst,
                productTypeCustomFieldsData: currentClaim.productTypeCustomFieldsData,
                claimOccurredAt: isoDateOfLoss ? DateTime.fromISO(isoDateOfLoss).toJSDate() : undefined,
                // Idempotency key (CUR-398). This demo route deliberately has no
                // polling/panel — a duplicate submit surfaces the CONFLICT message.
                submissionId: currentClaim.submissionId,
                // Signed vehicle proof from the lookup (CUR-494) — the server
                // verifies it and sets Claim.fleetVehicleId.
                fleetVehicleProof: currentClaim.fleetVehicleProof,
            });

            // The claim is lodged either way — report it before the fence below decides
            // whether this browser still has a form to show it in.
            capture('claim_lodgement_submitted', { entity_id: result.id.toString() });

            // Writes the identifiers back, or — if "Start again" ran while this was in
            // flight — leaves the cleared form alone and tells the claimant their claim
            // was lodged. Extracted so both branches are unit-testable.
            applySubmissionResult({
                result,
                submissionFence,
                submissionFenceRef,
                formStore,
                goToNextForm,
            });
        } catch (e) {
            errorMessage.show(e);
        } finally {
            setIsSubmitting(false);
        }
    });

    /**
     * Submitting is a two-stage affair: the guards must engage on the click, the request may
     * wait out the 500 ms debounce. "Start again" is disabled by `isSubmitting` and fenced by
     * `submissionFenceRef`, so taking either inside the debounced body would leave the reset
     * available for the first half-second of a submit the claimant can already see spinning —
     * and the lodged claim would then be dropped with nothing shown to them.
     */
    const save = () => {
        setIsSubmitting(true);
        return submitClaim(submissionFenceRef.current);
    };

    // An authoritative NOT_FOUND tuple is always terminal — see the sibling lodgement route. A
    // non-NOT_FOUND readiness failure after initialisation is retryable and must not unmount an
    // in-progress form.
    const tupleNotFound = readinessError instanceof TRPCClientError && readinessError.data?.code === 'NOT_FOUND';

    if (!isPdsVersionEnabled || tupleNotFound) {
        return <DemoNotAvailable />;
    }

    if (readinessError && !isInitialized) {
        return (
            <InlineQueryError
                className="flex-1"
                error={readinessError}
                onRetry={retryReadiness}
                retryable
                title="Could not load claim form"
            />
        );
    }

    // Hold rendering until the questionnaire theme has loaded — rendering the
    // form chrome earlier paints it in default colours, then visibly snaps to
    // the org branding once the theme query resolves.
    if (loading) {
        return <Loading size="large" className="flex-1" />;
    }

    return (
        <PublicThemeProvider colorPrimary={uiTheme.colorPrimary}>
            <ConfigProvider theme={questionnaireFormTheme(newTheme, uiTheme)}>
                <div className="crm-custom-theme flex-1 md:px-0 lg:p-4">
                    <Row justify="center" className="pb-10">
                        <Col xs={24} sm={16} md={14} lg={12} xl={10} className="max-w-3xl">
                            {questionnaireLogoUrl && (
                                // Fixed-height box mirroring the production lodgement route —
                                // reserves the logo's space so it cannot shift the form (CLS).
                                <div className="mb-3 flex h-24 items-center justify-center text-center">
                                    <img
                                        src={questionnaireLogoUrl}
                                        className="m-auto max-h-full max-w-full"
                                        alt="Questionnaire logo"
                                        fetchPriority="high"
                                    />
                                </div>
                            )}
                            <div
                                className="flex justify-center font-semibold text-foreground"
                                style={{ fontSize: `${baseFontSize * 1.5}px` }}
                            >
                                Claim Lodgement
                            </div>
                            {(!!(backButtonLabel && backButtonUrl) || activeForm < CONFIRMATION_FORM) && (
                                <div className="mt-2 mb-2 flex flex-wrap justify-center gap-2 md:flex-nowrap md:justify-between">
                                    {backButtonLabel && backButtonUrl && (
                                        <Button
                                            type="primary"
                                            icon={<ArrowLeft />}
                                            onClick={() => {
                                                // http(s) only — see isSafeBackButtonUrl.
                                                if (!isSafeBackButtonUrl(backButtonUrl)) {
                                                    message.error('Something went wrong. Please try again later.');
                                                    return;
                                                }
                                                window.location.href = backButtonUrl;
                                            }}
                                        >
                                            {backButtonLabel}
                                        </Button>
                                    )}
                                    {activeForm < CONFIRMATION_FORM && (
                                        <Button
                                            icon={<RotateCcw />}
                                            // A claim being lodged right now must not be discarded
                                            // out from under the submission.
                                            disabled={isSubmitting}
                                            onClick={() => void handleStartAgain()}
                                        >
                                            Start again
                                        </Button>
                                    )}
                                </div>
                            )}
                            <Divider className="mt-2 mb-4" />
                            {verifiedMatch && activeForm > LOOKUP_FORM && activeForm < REVIEW_FORM && (
                                <div className="mb-4 rounded-lg border border-separator bg-muted px-4 py-3 text-muted-foreground text-sm">
                                    <span className="font-medium text-foreground">Vehicle:</span>{' '}
                                    {[
                                        formatFleetVehicleName(verifiedMatch.vehicle),
                                        formatFleetRegistration(verifiedMatch.vehicle),
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}{' '}
                                    · <span className="font-medium text-foreground">Driver:</span>{' '}
                                    {verifiedMatch.driver.name}
                                </div>
                            )}
                            <div key={`${activeForm}-${resetVersion}`}>
                                {activeForm === LOOKUP_FORM && (
                                    <VehicleDriverLookup
                                        orgId={orgId}
                                        goForward={goNextOrReturnToReview}
                                        updateClaim={updateClaim}
                                        onVerified={setVerifiedMatch}
                                    />
                                )}
                                {activeForm === QUESTIONNAIRE_FORM && (
                                    <Questionnaire
                                        onSave={save}
                                        readOnly={readOnly}
                                        goBack={goToPrevFormAndCancelReturn}
                                        goNext={goNextOrReturnToReview}
                                        questionnaireStore={questionnaireStore}
                                        displaySaveButton={false}
                                        isInitialized={isInitialized}
                                        isPublic
                                        onActiveStepIncrement={incrementActiveStep}
                                        onActiveStepDecrement={decrementActiveStep}
                                        persistServerCheckpoint={persistServerCheckpoint}
                                        // "Start again" sits in the chrome above the questionnaire
                                        // and stays clickable through the vehicle scan's multi-await
                                        // launch, so the card must be able to tell whether the
                                        // journey it started on is still the current one.
                                        beginCheckpointJourney={beginCheckpointJourney}
                                        lodgement={{
                                            orgId: String(orgId),
                                            questionnaireId: String(questionnaireId),
                                            pdsVersionId: String(pdsVersionId),
                                            productTypeId: String(productTypeId),
                                            // Only trust this flow's returning-claimant identity. Form state can
                                            // contain a foreign draft restored from the live flow's shared snapshot.
                                            draftId,
                                            submissionId: submissionId ?? undefined,
                                            // Bring the claimant back to THIS flow after the scan,
                                            // not to the live lodgement route's empty form.
                                            surface: 'lodgement-lookup',
                                        }}
                                    />
                                )}
                                {activeForm === ADDITIONAL_INFO_FORM && (
                                    <AdditionalInfo
                                        readOnly={readOnly}
                                        goBack={goToPrevFormAndCancelReturn}
                                        goNext={goNextOrReturnToReview}
                                        additionalInfo={additionalInfo}
                                        updateClaim={updateClaim}
                                    />
                                )}
                                {activeForm === REVIEW_FORM && (
                                    <ReviewDeclaration
                                        questionnaireStore={questionnaireStore}
                                        verifiedMatch={verifiedMatch}
                                        fleetVehicleSnapshot={fleetVehicleSnapshot}
                                        claimantRole={claimantRole}
                                        primaryContact={primaryContact}
                                        additionalInfo={additionalInfo}
                                        questionnaireDeclarationTitle={questionnaireDeclarationTitle}
                                        questionnaireDeclarationBody={questionnaireDeclarationBody}
                                        claimNotResultOfIllegalAct={claimNotResultOfIllegalAct}
                                        updateClaim={updateClaim}
                                        goBack={goToPrevForm}
                                        onEditLookup={() => {
                                            setReturningToReview(true);
                                            setActiveForm(LOOKUP_FORM);
                                        }}
                                        onEditSection={(label) => {
                                            setReturningToReview(true);
                                            jumpToSection(label);
                                            setActiveForm(QUESTIONNAIRE_FORM);
                                        }}
                                        onEditAdditionalInfo={() => {
                                            setReturningToReview(true);
                                            setActiveForm(ADDITIONAL_INFO_FORM);
                                        }}
                                        onSubmit={save}
                                        readOnly={readOnly}
                                    />
                                )}
                                {activeForm === CONFIRMATION_FORM && (
                                    <Confirmation
                                        claimGeneratedId={claimGeneratedId}
                                        questionnaireConfirmationBody={questionnaireConfirmationBody}
                                        questionnaireConfirmationTemplateData={questionnaireConfirmationTemplateData}
                                    />
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>
            </ConfigProvider>
        </PublicThemeProvider>
    );
}

export const Route = createFileRoute(
    '/public/$orgId/claim/lodgement-lookup/$pdsVersionId/$questionnaireId/$productTypeId',
)({
    // Espirito FNOL PoC demo — never expose on production. Blocked before the
    // component mounts (and before any data fetch); dev + staging stay open for
    // the demo. See the file header for why this is an env gate, not a flag.
    beforeLoad: (): void => {
        if (getConfig('ENV', import.meta.env.VITE_ENV) === 'production') {
            throw redirect({ to: '/not-found', replace: true });
        }
    },
    validateSearch: zodValidator(
        z.object({
            // Carried by the vehicle-scan completion redirect (see the handoff redemption in
            // vehicle-inspection-service): they address the silent server checkpoint the
            // claim was saved into before it navigated away.
            draftId: z.string().optional(),
            accessKey: z.string().optional(),
            scan: z.string().optional(),
        }),
    ),
    params: {
        parse(params) {
            return {
                pdsVersionId: parseBigintRouteParam(params.pdsVersionId),
                questionnaireId: parseBigintRouteParam(params.questionnaireId),
                productTypeId: parseBigintRouteParam(params.productTypeId),
            };
        },
    },
    component: ClaimLodgementLookup,
});
